import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { api, imageUrl, API_BASE, formatErr } from "@/lib/api";
import UploadZone from "@/components/UploadZone";
import ProcessingAnimation from "@/components/ProcessingAnimation";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import { contrastText, BRAND_DEFAULT } from "@/lib/color";
import { ArrowRight, Sparkles, Phone, Lock } from "lucide-react";

const STEPS = { intro: 0, form: 1, processing: 2, result: 3, notfound: -1, unavailable: 97, locked: 98, demo: 99 };

export default function Embed() {
  const { slug } = useParams();
  const [clinic, setClinic] = useState(null);
  const [step, setStep] = useState(STEPS.intro);
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState("");
  const [form, setForm] = useState({ patient_name: "", patient_email: "", patient_phone: "" });
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const rootRef = useRef(null);
  const brand = clinic?.primary_color || BRAND_DEFAULT;
  const brandStyle = { backgroundColor: brand, color: contrastText(brand), borderColor: brand };

  useEffect(() => {
    api
      .get(`/clinics/${slug}/public`)
      .then((res) => {
        setClinic(res.data);
        if (res.data?.is_marketing_demo) setStep(STEPS.demo);
        else if (res.data?.read_only) setStep(STEPS.unavailable);
        else if (!res.data?.embed_enabled) setStep(STEPS.locked);
      })
      .catch(() => setStep(STEPS.notfound));
  }, [slug]);

  useEffect(() => {
    if (!file) {
      setFilePreview("");
      return;
    }
    const url = URL.createObjectURL(file);
    setFilePreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  // Post height to parent so the iframe auto-sizes
  useLayoutEffect(() => {
    if (!rootRef.current) return;
    const post = () => {
      try {
        const h = rootRef.current?.scrollHeight || document.body.scrollHeight;
        window.parent?.postMessage({ type: "toothpix:resize", slug, height: h }, "*");
      } catch (err) {
        // Cross-origin postMessage can throw in rare iframe sandboxing setups; non-fatal.
        if (typeof console !== "undefined") console.debug("toothpix resize post failed", err);
      }
    };
    post();
    const ro = new ResizeObserver(post);
    ro.observe(rootRef.current);
    return () => {
      ro.disconnect();
    };
  }, [slug, step, file, result, clinic]);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) {
      setErr("Please upload a smiling photo");
      return;
    }
    if (!form.patient_name || !form.patient_email) {
      setErr("Please add your name and email so the clinic can reach you.");
      return;
    }
    setErr("");
    setStep(STEPS.processing);
    try {
      const fd = new FormData();
      fd.append("patient_name", form.patient_name);
      fd.append("patient_email", form.patient_email);
      fd.append("patient_phone", form.patient_phone || "");
      fd.append("image", file);
      const { data } = await axios.post(`${API_BASE}/clinics/${slug}/preview`, fd, { timeout: 180000 });
      setResult(data);
      setStep(STEPS.result);
    } catch (e2) {
      setErr(formatErr(e2.response?.data?.detail) || "Something went wrong while generating your smile.");
      setStep(STEPS.form);
    }
  };

  if (step === STEPS.notfound) {
    return (
      <Shell rootRef={rootRef}>
        <div className="text-center py-10">
          <div className="overline">404</div>
          <h1 className="font-heading text-3xl text-forest-700 mt-3">Clinic not found</h1>
        </div>
      </Shell>
    );
  }
  if (step === STEPS.demo) {
    return (
      <Shell rootRef={rootRef}>
        <LockedNotice
          title="Marketing demo only"
          body="This is the public demo clinic. To enable patient uploads, the clinic owner needs to create a Toothpix account."
          ctaText="Create a clinic"
          ctaHref={`${window.location.origin.replace(/\/$/, "")}/signup`}
        />
      </Shell>
    );
  }
  if (step === STEPS.unavailable) {
    return (
      <Shell rootRef={rootRef}>
        <div className="text-center py-10" data-testid="embed-unavailable">
          <Sparkles size={26} strokeWidth={1.3} className="text-forest-600 mx-auto" />
          <h1 className="font-heading text-2xl text-forest-700 mt-4">Smile previews are temporarily unavailable</h1>
          <p className="text-sm text-forest-400 mt-3 max-w-sm mx-auto">
            {clinic?.clinic_name ? `${clinic.clinic_name} isn't` : "This clinic isn't"} taking new smile
            previews right now. Call us to book a consultation. We'd love to talk about your smile.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-2 justify-center">
            {clinic?.booking_url && (
              <a href={clinic.booking_url} target="_blank" rel="noreferrer" className="btn-primary inline-flex" style={brandStyle} data-testid="embed-unavailable-book">
                Book a consultation <ArrowRight size={14} />
              </a>
            )}
            {clinic?.phone && (
              <a href={`tel:${clinic.phone}`} className="btn-secondary inline-flex" data-testid="embed-unavailable-call">
                <Phone size={14} /> {clinic.phone}
              </a>
            )}
          </div>
        </div>
      </Shell>
    );
  }
  if (step === STEPS.locked) {
    return (
      <Shell rootRef={rootRef}>
        <LockedNotice
          title="Embed isn't active for this clinic"
          body={`${clinic?.clinic_name || "This clinic"} is on the free trial. The embeddable widget unlocks on the Clinic plan.`}
          ctaText="Upgrade to enable embed"
          ctaHref={`${window.location.origin.replace(/\/$/, "")}/dashboard`}
        />
      </Shell>
    );
  }

  if (!clinic) {
    return (
      <Shell rootRef={rootRef}>
        <div className="text-center py-10 overline">Loading…</div>
      </Shell>
    );
  }

  return (
    <Shell rootRef={rootRef}>
      <ClinicHeader clinic={clinic} />
      <AnimatePresence mode="wait">
        {step === STEPS.intro && (
          <motion.div key="intro" {...fade} className="mt-6">
            <div className="overline">A gift from {clinic.clinic_name}</div>
            <h1 className="font-heading text-3xl md:text-4xl text-forest-700 mt-3 leading-tight">
              See your future smile.
            </h1>
            <p className="text-sm text-forest-400 mt-3">
              Upload a smiling selfie. In ~30 seconds, our clinical AI will
              show you what your teeth could look like after {clinic.treatment_name || "clear aligner"}
              {" "}treatment with {clinic.clinic_name}.
            </p>
            <button onClick={() => setStep(STEPS.form)} data-testid="embed-start" className="btn-primary mt-6" style={brandStyle}>
              Start my preview <ArrowRight size={16} />
            </button>
          </motion.div>
        )}

        {step === STEPS.form && (
          <motion.form key="form" {...fade} onSubmit={submit} className="mt-6" data-testid="embed-form">
            <UploadZone file={file} onFileSelected={setFile} />
            <div className="mt-6 space-y-4">
              <Field label="Full name" value={form.patient_name} onChange={(v) => setForm({ ...form, patient_name: v })} required testid="embed-name" />
              <Field label="Email" type="email" value={form.patient_email} onChange={(v) => setForm({ ...form, patient_email: v })} required testid="embed-email" />
              <Field label="Phone (optional)" value={form.patient_phone} onChange={(v) => setForm({ ...form, patient_phone: v })} testid="embed-phone" />
            </div>
            {err && (
              <div className="text-sm text-clay-600 border border-clay-600/30 bg-clay-400/10 px-3 py-2 mt-5">{err}</div>
            )}
            <button type="submit" className="btn-primary mt-6" style={brandStyle} data-testid="embed-submit">
              Generate my smile preview <ArrowRight size={16} />
            </button>
          </motion.form>
        )}

        {step === STEPS.processing && (
          <motion.div key="processing" {...fade} className="mt-6">
            <ProcessingAnimation imageSrc={filePreview} />
          </motion.div>
        )}

        {step === STEPS.result && result && (
          <motion.div key="result" {...fade} className="mt-6 space-y-6" data-testid="embed-result">
            <div>
              <div className="overline">Your AI smile preview</div>
              <h1 className="font-heading text-3xl text-forest-700 mt-2">
                {form.patient_name.split(" ")[0]}, meet your future smile.
              </h1>
            </div>
            <BeforeAfterSlider beforeSrc={imageUrl(result.before_url)} afterSrc={imageUrl(result.after_url)} aspect="aspect-[4/5]" brandColor={brand} />
            <div className="p-5" style={{ backgroundColor: brand, color: contrastText(brand) }}>
              <div className="overline" style={{ color: contrastText(brand), opacity: 0.7 }}>Next step</div>
              <h3 className="font-heading text-xl mt-2">Book a free consultation with {clinic.clinic_name}.</h3>
              <div className="mt-4 flex flex-col sm:flex-row gap-2">
                {clinic.booking_url ? (
                  <a href={clinic.booking_url} target="_blank" rel="noreferrer" className="px-5 py-2.5 text-sm font-medium inline-flex items-center gap-2" style={{ backgroundColor: contrastText(brand), color: brand }} data-testid="embed-book-btn">
                    Book a free consult <ArrowRight size={14} />
                  </a>
                ) : null}
                {clinic.phone && (
                  <a href={`tel:${clinic.phone}`} className="px-5 py-2.5 text-sm inline-flex items-center gap-2 border" style={{ borderColor: contrastText(brand), color: contrastText(brand) }}>
                    <Phone size={14} /> {clinic.phone}
                  </a>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <ToothpixCredit />
    </Shell>
  );
}

const fade = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -6 },
  transition: { duration: 0.35, ease: "easeOut" },
};

function Shell({ rootRef, children }) {
  return (
    <div ref={rootRef} className="bg-transparent text-forest-700" style={{ fontFamily: '"Manrope", sans-serif' }}>
      <div className="max-w-xl mx-auto p-5 md:p-8 bg-bone-100 border border-bone-200">
        {children}
      </div>
    </div>
  );
}

function ClinicHeader({ clinic }) {
  return (
    <div className="flex items-center gap-3 pb-4 border-b border-bone-200">
      {clinic.logo_url ? (
        <img src={clinic.logo_url} alt="" className="w-8 h-8 object-contain" />
      ) : (
        <Sparkles size={18} strokeWidth={1.5} style={{ color: clinic.primary_color || "#1C352D" }} />
      )}
      <div>
        <div className="font-heading text-lg leading-none text-forest-700">{clinic.clinic_name}</div>
        <div className="overline mt-1">{clinic.clinic_tagline || "Clear Aligner Provider"}</div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testid }) {
  return (
    <div>
      <label className="label-flat">{label}</label>
      <input
        type={type}
        className="input-flat"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        data-testid={testid}
      />
    </div>
  );
}

function LockedNotice({ title, body, ctaText, ctaHref }) {
  return (
    <div className="text-center py-10">
      <Lock size={28} strokeWidth={1.3} className="text-forest-600 mx-auto" />
      <h1 className="font-heading text-2xl text-forest-700 mt-4">{title}</h1>
      <p className="text-sm text-forest-400 mt-3 max-w-sm mx-auto">{body}</p>
      <a href={ctaHref} target="_blank" rel="noreferrer" className="btn-primary mt-6 inline-flex">
        {ctaText} <ArrowRight size={14} />
      </a>
    </div>
  );
}

function ToothpixCredit() {
  return (
    <div className="mt-8 pt-4 border-t border-bone-200 text-[10px] text-forest-400 text-center uppercase tracking-[0.2em]">
      Powered by{" "}
      <a
        href={`${window.location.origin.replace(/\/$/, "")}/`}
        target="_blank"
        rel="noreferrer"
        className="text-forest-600"
      >
        Toothpix
      </a>
    </div>
  );
}
